import { ogImageMeta } from "@/lib/og-images";
import { renderHomeJsonLd } from "@/lib/agent-errors";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";

import { Hero } from "@/components/home/hero";
import { ClientLogos } from "@/components/home/client-logos";
import { Proof } from "@/components/home/proof";
import { HowWeWork } from "@/components/how-we-work";
import { SelectedWork } from "@/components/selected-work";
import { WhyUs } from "@/components/home/why-us";
import { PricingPlans } from "@/components/pricing-plans";
import { About } from "@/components/home/about";
import { LazyMount } from "@/components/lazy-mount";

// The footer is heavy on links and sits far below the fold; it ships as its
// own chunk and mounts when it comes near the viewport.
const SiteFooter = lazy(() =>
  import("@/components/site-footer").then((m) => ({ default: m.SiteFooter })),
);

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Idea to Live Product in 14 Days · Product Design Studio | Vibing Company" },
      {
        name: "description",
        content:
          "Full-stack designers who take websites and web apps from idea to live product in 14 days for a fixed €35,000. Studio-grade UI/UX, the whole build, and brand, motion and growth work on top.",
      },
      { property: "og:title", content: "From idea to live product in 14 days · Vibing Company" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      ...ogImageMeta("home", "Share card: product design studio, idea to live product in 14 days"),
      {
        property: "og:description",
        content:
          "Websites and web apps designed and built to a studio standard in 14 days. €35,000 fixed, one launch date, you own everything. Brand, motion and growth work from the same studio.",
      },
      { property: "og:url", content: "https://vibing.inc/" },
    ],
    links: [
      { rel: "canonical", href: "https://vibing.inc/" },
      { rel: "alternate", type: "text/plain", href: "https://vibing.inc/llms.txt" },
      { rel: "alternate", type: "text/markdown", href: "https://vibing.inc/md/" },
      { rel: "service-desc", type: "application/json", href: "https://vibing.inc/api/public/openapi.json" },
      { rel: "alternate", type: "application/json", href: "https://vibing.inc/.well-known/mcp.json", title: "MCP server manifest" },
      { rel: "help", href: "https://vibing.inc/docs" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(renderHomeJsonLd()),
      },
    ],
  }),

  component: Index,
});

function Index() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <main id="main" tabIndex={-1}>
        <Hero />
        <ClientLogos />
        <Proof />

        <HowWeWork />
        <SelectedWork />
        <WhyUs />
        <PricingPlans />
        <About />
      </main>
      <LazyMount minHeight={500}>
        <Suspense fallback={null}>
          <SiteFooter />
        </Suspense>
      </LazyMount>
    </div>
  );
}
